import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './useAuth';
import type { Database } from '../../database.types';

type WishlistInsert = Database['public']['Tables']['user_wishlist']['Insert'];
type WishlistProduct = {
  id: number;
  name: string;
  weight: number;
  quantity: number;
  is_hidden: boolean;
  sale_rate: number;
  usd_price: number;
  created_at: string;
  updated_at: string;
  category_id: number;
  description: string;
  image_paths: string[];
  is_featured: boolean;
  detail_value: string;
  sub_category_id: number;
  wishlist_added_at: string;
  product_measurements: any[];
};

interface UseWishlistReturn {
  wishlistItems: WishlistProduct[];
  wishlistCount: number;
  loading: boolean;
  error: string | null;
  addToWishlist: (productId: number) => Promise<boolean>;
  removeFromWishlist: (productId: number) => Promise<boolean>;
  isInWishlist: (productId: number) => boolean;
  refreshWishlist: () => Promise<void>;
  clearWishlist: () => Promise<boolean>;
}

export const useWishlist = (): UseWishlistReturn => {
  const { userId, user } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<WishlistProduct[]>([]);
  const [loading] = useState(false); // Set to false since we're not fetching in this hook
  const [error, setError] = useState<string | null>(null);

  const addToWishlist = useCallback(async (productId: number): Promise<boolean> => {
    if (!userId || !user) {
      setError('You must be logged in to add items to wishlist');
      return false;
    }

    try {
      setError(null);

      // Only check existing items if we have fetched the wishlist
      // When skipInitialFetch is true, we rely on the UI state instead
    
        const existingItem = wishlistItems.find(item => item.id === productId);
        if (existingItem) {
          setError('Item is already in your wishlist');
          return false;
        
      }

      const wishlistItem: WishlistInsert = {
        user_id: userId,
        product_id: productId,
        created_at: new Date().toISOString(),
      };

      const { error: insertError } = await supabase
        .from('user_wishlist')
        .insert(wishlistItem)
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Refresh the wishlist to get updated data
      // await fetchWishlist();
      return true;
    } catch (err) {
      // console.error('Error adding to wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to add to wishlist');
      return false;
    }
  }, [userId, user, wishlistItems]);

  // Add deleteUserWishlistV2 function
const deleteUserWishlistV2 = async (payload: Record<string, any>) => {
    const { data, error } = await supabase.rpc('delete_user_wishlist_v2', { payload });
    if (error) throw error;
    return data;
  };

  const removeFromWishlist = useCallback(async (productId: number): Promise<boolean> => {
    if (!userId || !user) {
      setError('You must be logged in to remove items from wishlist');
      return false;
    }

    try {
      setError(null);
      // Use the RPC function
      await deleteUserWishlistV2({ user_id: userId, product_id: productId });
      
      // Refresh the wishlist to get updated data
      // await fetchWishlist();
      return true;
    } catch (err) {
      // console.error('Error removing from wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove from wishlist');
      return false;
    }
  }, [userId, user]);

  const isInWishlist = useCallback((productId: number): boolean => {
    return wishlistItems.some(item => item.id === productId);
  }, [wishlistItems]);

  const clearWishlist = useCallback(async (): Promise<boolean> => {
    if (!userId || !user) {
      setError('You must be logged in to clear wishlist');
      return false;
    }

    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('user_wishlist')
        .delete()
        .eq('user_id', userId);

      if (deleteError) {
        throw deleteError;
      }

      setWishlistItems([]);
      return true;
    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to clear wishlist');
      return false;
    }
  }, [userId, user]);

  const refreshWishlist = useCallback(async () => {
    // await fetchWishlist();
  }, []);
  useEffect(() => {
    // fetchWishlist();
  }, [userId]);

  return {
    wishlistItems,
    wishlistCount: wishlistItems.length,
    loading,
    error,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    refreshWishlist,
    clearWishlist,    
  };
};

// Fetch wishlist using get_user_wishlist_v2h RPC
export const getUserWishlistV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc('get_user_wishlist_v2h', { payload });
  if (error) throw error;
  // Return the full response object
  return data;
};

// Delete wishlist using delete_user_wishlist_v2 RPC
export const deleteUserWishlistV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc('delete_user_wishlist_v2', { payload });
  if (error) throw error;
  return data;
};

export default useWishlist;
